import { createSteeringURL } from '@/api'
import { steeringAuthInit } from '@/stores/auth'
import { Image, ImageCreateData, ImageUpdateData } from '@/types'
import { aggregateWithIdsParameter, useStoreHelpers } from '@/util'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
} from '@rokoli/bnb/drf'
import { useDevicePixelRatio } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, toValue } from 'vue'

const endpoint = createSteeringURL.prefix('images')

export const useImageStore = defineStore('images', () => {
  const { api, base } = createExtendableAPI<Image>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })
  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APICreate<Image, ImageCreateData | FormData>(api),
    ...APIUpdate<Image, ImageUpdateData | FormData, Partial<ImageUpdateData> | FormData>(api),
    ...APIRemove(api),
    ...useStoreHelpers({ ...base, ...listOperations, ...retrieveOperations }),
  }
})

type UseImageUrlOptions = {
  width?: MaybeRefOrGetter<number | null | undefined>
  height?: MaybeRefOrGetter<number | null | undefined>
}

export function useImageUrl(
  image: MaybeRefOrGetter<Image | Image['id'] | null | undefined>,
  options: UseImageUrlOptions | undefined = undefined,
) {
  const { pixelRatio } = useDevicePixelRatio()
  return computed(() => {
    const _image = toValue(image)
    if (!_image) return null
    const imageId = typeof _image === 'number' ? _image : _image.id

    const queryArgs = new URLSearchParams()
    const width = toValue(options?.width)
    const height = toValue(options?.height)
    const ratio = pixelRatio.value
    if (width) queryArgs.set('width', Math.round(toValue(width) * ratio).toString())
    if (height) queryArgs.set('height', Math.round(toValue(height) * ratio).toString())

    return endpoint(imageId, 'render', queryArgs)
  })
}
